import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

// Cache for image URLs to avoid repeated Supabase calls
const imageUrlCache: { [key: string]: string } = {};

/**
 * Ensures storage bucket exists - checks if it exists
 * @returns Promise resolving to true if bucket exists
 */
export const ensureStorageBucketExists = async (): Promise<boolean> => {
  try {
    logger.debug("🔍 Checking if product-images bucket exists...");
    
    // Check if bucket exists
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      logger.warn('Error checking buckets:', listError);
      return false;
    }
    
    const productImagesBucket = buckets?.find(bucket => bucket.name === 'product-images');
    
    if (productImagesBucket) {
      logger.debug('✅ Product images bucket exists');
      return true;
    } else {
      logger.warn('❌ Product images bucket not found');
      return false;
    }
  } catch (error) {
    logger.warn('Exception during bucket check:', error);
    return false;
  }
};

/**
 * Resolves any image URL to a fully qualified URL with enhanced error handling
 * Handles legacy image paths, full URLs, and relative paths
 */
export const getProductImageUrl = (imageUrl: string | null): string => {
  if (!imageUrl || imageUrl === "") {
    return "/placeholder.svg";
  }
  
  // If it's a placeholder or already a full URL, return it directly
  if (imageUrl === "/placeholder.svg" || imageUrl.startsWith('http')) {
    return imageUrl;
  }
  
  // Check cache first to avoid redundant processing
  if (imageUrlCache[imageUrl]) {
    return imageUrlCache[imageUrl];
  }
  
  // Debug log
  logger.debug("Processing image path:", imageUrl);
  
  // Handle legacy image paths that might include the bucket name
  let finalPath = imageUrl;
  if (imageUrl.startsWith('product-images/')) {
    finalPath = imageUrl.replace('product-images/', '');
  }
  
  // Clean the path - remove any leading slashes or duplicated bucket names
  finalPath = finalPath.replace(/^\/+/, '').replace(/^product-images\//, '');
  
  // Try to get the URL using Supabase's getPublicUrl with better error handling
  try {
    const { data } = supabase.storage
      .from('product-images')
      .getPublicUrl(finalPath);
    
    const publicUrl = data?.publicUrl;
    
    if (publicUrl && publicUrl !== 'undefined' && !publicUrl.includes('undefined')) {
      // Validate the URL format
      try {
        new URL(publicUrl);
        // Cache the result for future use
        imageUrlCache[imageUrl] = publicUrl;
        logger.debug("Generated public URL:", publicUrl);
        return publicUrl;
      } catch (urlError) {
        logger.warn("Invalid URL generated:", publicUrl, urlError);
      }
    }
  } catch (error) {
    logger.warn("Error generating public URL:", error, "for image:", imageUrl);
  }
  
  // Enhanced fallback: Direct URL construction with validation
  try {
    const directUrl = `https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/${finalPath}`;
    
    // Basic URL validation
    try {
      new URL(directUrl);
      // Cache the result for future use
      imageUrlCache[imageUrl] = directUrl;
      logger.debug("Generated direct URL:", directUrl);
      return directUrl;
    } catch (urlError) {
      logger.warn("Invalid direct URL:", directUrl, urlError);
    }
  } catch (error) {
    logger.warn("Error generating direct URL:", error, "for image:", imageUrl);
  }
  
  // Final fallback
  logger.warn("All URL generation methods failed for:", imageUrl, "using placeholder");
  return "/placeholder.svg";
};

// Clear image URL from cache - useful after uploads
export const clearImageUrlCache = (imageUrl?: string): void => {
  if (imageUrl && imageUrlCache[imageUrl]) {
    delete imageUrlCache[imageUrl];
    logger.debug("Cleared cache for image:", imageUrl);
  } else if (!imageUrl) {
    // Clear entire cache if no specific URL is provided
    const cacheSize = Object.keys(imageUrlCache).length;
    Object.keys(imageUrlCache).forEach(key => {
      delete imageUrlCache[key];
    });
    logger.debug(`Cleared entire image URL cache (${cacheSize} entries)`);
  }
};

/**
 * Extract product images with consistent format from any product object
 * Handles both legacy image_url and new product_images array
 */
export const getProductImages = (product: any) => {
  if (!product) return [];
  
  try {
    // If product has product_images array, use it
    if (product.product_images && Array.isArray(product.product_images) && product.product_images.length > 0) {
      // Sort by display_order if available
      const sortedImages = [...product.product_images]
        .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
        
      return sortedImages.map(img => ({
        ...img,
        image_url: getProductImageUrl(img.image_url) // Ensure URLs are fully formed
      }));
    }
    
    // Otherwise, create a single image entry from the image_url if it exists
    if (product.image_url) {
      return [{ 
        id: 'legacy-image', 
        image_url: getProductImageUrl(product.image_url), 
        is_primary: true,
        display_order: 0
      }];
    }
    
    // Return empty array if no images
    return [];
  } catch (error) {
    logger.warn("Error processing product images:", error, "for product:", product?.id);
    return [];
  }
};

/**
 * Gets the primary image URL for a product with enhanced fallback
 * Handles both legacy image_url and new product_images array
 */
export const getPrimaryImageUrl = (product: any): string => {
  try {
    // Basic validation
    if (!product) return "/placeholder.svg";
    
    // Get all images using the helper function
    const images = getProductImages(product);
    
    if (!images || images.length === 0) return "/placeholder.svg";
    
    // Find primary image
    const primaryImage = images.find(img => img.is_primary);
    
    // Return primary image URL or first image URL
    const selectedImage = primaryImage || images[0];
    const imageUrl = selectedImage.image_url;
    
    // Additional validation - ensure we don't return undefined or invalid URLs
    if (!imageUrl || imageUrl === 'undefined' || imageUrl === 'null') {
      logger.warn("Invalid primary image URL for product:", product?.id, "got:", imageUrl);
      return "/placeholder.svg";
    }
    
    return imageUrl;
  } catch (error) {
    logger.warn("Error getting primary image:", error, "for product:", product?.id);
    return "/placeholder.svg";
  }
};

// Simplified version when you have already extracted the images array
export const getPrimaryImageFromArray = (images: Array<{ image_url: string, is_primary: boolean }> | null): string => {
  if (!images || images.length === 0) return "/placeholder.svg";
  
  try {
    // Find primary image
    const primaryImage = images.find(img => img.is_primary);
    
    // Return primary image URL or first image URL
    const imageUrl = primaryImage ? primaryImage.image_url : images[0].image_url;
    
    // Ensure the URL is fully formed
    return getProductImageUrl(imageUrl);
  } catch (error) {
    logger.error("Error getting primary image from array:", error);
    return "/placeholder.svg";
  }
};

// Preload high-priority images to improve LCP
export const preloadCriticalImages = (imageUrls: string[]) => {
  imageUrls.forEach(url => {
    if (!url || url === "/placeholder.svg") return;
    
    const img = new Image();
    img.src = url;
    (img as any).fetchpriority = "high";
    img.decoding = "async";
    img.crossOrigin = "anonymous"; // Add cross-origin attribute
  });
};

// Helper to generate image srcset for responsive images
export const generateSrcSet = (imageUrl: string): string => {
  if (!imageUrl || imageUrl === "/placeholder.svg") return imageUrl;
  
  // For this implementation, we're assuming Supabase doesn't have
  // automatic image resizing. In a real implementation with a CDN
  // that supports image resizing, you'd generate multiple sizes.
  return imageUrl;
};

/**
 * Debug utility to check storage status
 * Can be called from components to diagnose image issues
 */
export const debugStorageStatus = async () => {
  try {
    logger.debug("🔍 Checking Supabase storage status...");
    
    // Check if bucket exists
    const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
    if (bucketsError) {
      logger.error("❌ Error listing buckets:", bucketsError);
      return {
        success: false,
        error: bucketsError,
        message: "Failed to list storage buckets"
      };
    }
    
    const productBucket = buckets?.find(b => b.name === 'product-images');
    if (!productBucket) {
      logger.warn("❌ 'product-images' bucket not found");
      return {
        success: false,
        message: "Product images bucket not found - this should have been created"
      };
    }
    
    logger.debug("✅ Found product-images bucket:", productBucket);
    
    // Try to get a list of images to verify permissions
    try {
      const { data: files, error: filesError } = await supabase.storage
        .from('product-images')
        .list('', { limit: 5 });
        
      if (filesError) {
        logger.error("❌ Error listing files in bucket:", filesError);
        
        // Try direct URL access as fallback
        logger.debug("Testing direct URL access as alternative...");
        const testDirectUrl = `https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/test.jpg`;
        
        try {
          const response = await fetch(testDirectUrl, { method: 'HEAD' });
          logger.debug(`Direct URL test status: ${response.status}`);
          if (response.status !== 404) {
            logger.debug("✅ Direct bucket access works despite API errors");
            return {
              success: true,
              bucketExists: true,
              directAccess: true,
              message: "Direct URL access to bucket works, but API access has issues",
              bucketDetails: productBucket
            };
          }
        } catch (directError) {
          logger.error("Direct URL test failed:", directError);
        }
        
        return {
          success: false,
          error: filesError,
          message: "Failed to list files in product-images bucket"
        };
      }
      
      logger.debug(`✅ Listed ${files?.length || 0} files in product-images bucket`);
      
      // Check a random file to ensure public URL generation works
      if (files && files.length > 0) {
        const testFile = files[0];
        const { data } = supabase.storage
          .from('product-images')
          .getPublicUrl(testFile.name);
          
        logger.debug("✅ Example public URL:", data?.publicUrl);
        
        // Try to fetch the image to verify it's accessible
        try {
          const response = await fetch(data?.publicUrl || '', { method: 'HEAD' });
          logger.debug(`✅ Public URL test: ${response.status === 200 ? 'accessible' : 'not accessible'} (Status: ${response.status})`);
        } catch (fetchError) {
          logger.error("❌ Error testing public URL fetch:", fetchError);
        }
      }
      
      return {
        success: true,
        bucketExists: true,
        fileCount: files?.length || 0,
        message: "Storage bucket check successful",
        bucketDetails: productBucket,
        fileList: files?.length ? files.map(f => f.name) : []
      };
    } catch (error) {
      logger.error("❌ Error checking files:", error);
      return {
        success: false,
        error,
        message: "Error accessing files in the bucket"
      };
    }
  } catch (error) {
    logger.error("❌ Unexpected error checking storage:", error);
    return {
      success: false,
      error,
      message: "Unexpected error checking storage status"
    };
  }
};
